/**
 * The world datum, the landform height model, its children, and the ground it is made of.
 *
 * Defaults here are the values the generator starts from (DEFAULT_TERRAIN_LAYER_CONFIG,
 * DEFAULT_TERRAIN_CONFIG.materialLayers), and `catalog.test.ts` asserts that pairing so the two
 * cannot drift while both exist. Descriptions moved out of NewWorldDialog verbatim.
 *
 * TWO THINGS THAT USED TO BE THREE NODES. `base` was a group whose enum chose between `flat` and
 * `landform`, which is a tickbox written as a subtree selector: the two were never independent (the
 * generator forces landform off beside flat), so `landform` now carries the switch and unticking it
 * IS the flat world. That deletes the only case the editor needed a subtree-selecting enum for.
 *
 * AND ONE DATUM, NOT TWO. `groundLevel` (64) and `groundLevel` (40) were the same quantity under
 * two names — the level every column is measured from — kept apart only because the two height
 * models were built apart. `world.groundLevel` is both. Unified DOWNWARD at 40 deliberately: the
 * default world is the landform one, so every landform guard in the repo stays green and only the
 * flat-world baseline re-captures.
 */
import { DEFAULT_LANDFORM_CURVE } from '../../Curve.js';
export const WORLD = {
    kind: 'world',
    label: 'World',
    blurb: 'The scale everything else is measured against, and the level it stands on.',
    capability: 'group',
    maxLod: -1,
    expandAtLod: 99,
    // PATHS SIT WITH THE LAND, not under Regions. They take their endpoints from region nodes, but
    // what they DO is bench the terrain — a field over the whole world, like landform's own layers —
    // and reading them as a region sub-feature buried the dial for the one system whose cost is
    // paid by every column of every world (see PathsConfig.wanderM).
    children: ['landform', 'paths', 'strata', 'regions'],
    params: [
        { t: 'number', key: 'masterScale', label: 'World scale', min: 0.1, max: 1, step: 0.05, def: 1, unit: 'x',
            desc: 'Size of everything — land, buildings, regions. Higher = bigger; 1 = full size.' },
        { t: 'number', key: 'groundLevel', label: 'Ground level', min: 2, max: 64, step: 1, def: 10, unit: 'm',
            desc: 'The level the world is measured from — flat ground, or sea level under landforms.' },
    ],
};
export const LANDFORM = {
    kind: 'landform',
    label: 'Landforms',
    blurb: 'Sea, beaches, plains and mountains, with the surface skinned to match. Off is a flat '
        + 'world at Ground level — the cheapest one the generator can make.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: 99,
    // Phase order: the two height modifiers, then the water, then the skin that reads the finished
    // surface. A child composes after its parent and after its earlier siblings (see columnPhases).
    children: ['terrace', 'detail', 'sea', 'surface'],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'landScale', label: 'Land scale', min: 0.1, max: 2, step: 0.1, def: 1, unit: 'x',
            desc: 'Size of the land only, on top of World scale. Higher = bigger landmasses.' },
        { t: 'number', key: 'mountainHeight', label: 'Mountain height', min: 50, max: 4000, step: 25, def: 1000, unit: 'm',
            desc: 'Peak height above sea level. Taller mountains are wider too, so slopes hold.' },
        { t: 'number', key: 'scale', label: 'Feature scale', min: 0.25, max: 4, step: 0.25, def: 1, unit: 'x',
            desc: 'Stretches the landforms horizontally. Higher = smaller, steeper continents; 1 = natural.' },
        { t: 'number', key: 'seaCoveragePercent', label: 'Sea coverage', min: 0, max: 90, step: 5, def: 30, unit: '%',
            desc: 'Percent of the world that is ocean. Higher = more sea, less land.' },
        { t: 'number', key: 'warpScale', label: 'Coast warp scale', min: 0.4, max: 8, step: 0.1, def: 1,
            desc: 'Coastline wiggle frequency. Higher = finer, more detailed coasts.' },
        // Past ~1 the warp stops displacing and starts DECORRELATING: landmasses dissolve into thin
        // swirled ribbons. High values are the reference's territory and pair with Ridged mountains.
        { t: 'number', key: 'warpFraction', label: 'Coast warp amount', min: 0, max: 8, step: 0.05, def: 0.5,
            desc: 'How far coasts bend, in wavelengths. ~0.5 gives sinuous bays; past 1 they dissolve.' },
        // Renamed from "Flat land": with the height model itself a tickbox, a knob called Flat land
        // inside Landforms reads as a contradiction. Same parameter, same noise field.
        { t: 'number', key: 'flatnessAmount', label: 'Terrain variation', min: 0, max: 100, step: 5, def: 100, unit: '%',
            desc: 'How strongly the world varies between plains and mountains. Peak height is unaffected.' },
        { t: 'number', key: 'flatnessScale', label: 'Variation scale', min: 0.25, max: 16, step: 0.25, def: 2,
            desc: 'Size of the plains-vs-mountains regions. Higher = smaller regions.' },
        { t: 'number', key: 'ridged', label: 'Ridged mountains', min: 0, max: 100, step: 5, def: 0, unit: '%',
            desc: 'Blends toward sharp alpine ridgelines. 0 = rounded hills; above 0 costs time.' },
        { t: 'number', key: 'seaDepthFrac', label: 'Sea depth', min: 2, max: 50, step: 1, def: 15, unit: '%',
            desc: 'Ocean floor depth, as a fraction of mountain height. Higher = deeper oceans.' },
        { t: 'number', key: 'beachWidthFrac', label: 'Beach width', min: 0, max: 5, step: 0.25, def: 0.5, unit: '%',
            desc: 'How far the flat beach rises above water. Higher = wider beaches.' },
        { t: 'number', key: 'octaves', label: 'Landform detail', min: 3, max: 12, step: 1, def: 10,
            desc: 'Layers of shape detail. Higher = craggier at every scale, and linearly slower.' },
        { t: 'curve', key: 'curve', label: 'Elevation curve', def: DEFAULT_LANDFORM_CURVE.map((p) => ({ ...p })),
            desc: 'Maps the continental noise to a height. Drag the points to reshape sea and mountains.' },
    ],
};
export const TERRACE = {
    kind: 'terrace',
    label: 'Cliff steps',
    blurb: 'Quantises the land onto flat treads separated by near-vertical risers, which the slope rule '
        + 'then paints as rock bands. Masked off near the waterline.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'stepFrac', label: 'Step height', min: 0, max: 5, step: 0.25, def: 2, unit: '%',
            desc: 'Terrace step height, as a fraction of mountain height. 0 = smooth.' },
        { t: 'number', key: 'coverage', label: 'Coverage', min: 0, max: 100, step: 5, def: 25, unit: '%',
            desc: 'How much of the world is terraced. Low = a few stepped districts among smooth hills.' },
        { t: 'number', key: 'sharpness', label: 'Sharpness', min: 1, max: 64, step: 1, def: 32,
            desc: 'How abrupt a riser is. 1 = no steps at all; high = near-vertical cliff faces.' },
        { t: 'number', key: 'mix', label: 'Strength', min: 0, max: 100, step: 5, def: 75, unit: '%',
            desc: 'Blend between stepped and smooth. 1 = flat treads; lower leaves treads sloped.' },
        { t: 'number', key: 'wobble', label: 'Wobble', min: 0, max: 30, step: 1, def: 10, unit: '%',
            desc: 'Undulates the treads so they are not dead-flat shelves. 0 = machined-looking.' },
        { t: 'number', key: 'maskScale', label: 'District size', min: 1, max: 64, step: 1, def: 16,
            desc: 'Size of a stepped district. Higher = smaller districts.' },
        { t: 'number', key: 'wobbleScale', label: 'Wobble scale', min: 4, max: 256, step: 4, def: 64,
            desc: 'Wobble wavelength. Higher = finer undulation, so treads tilt and dome.' },
    ],
};
export const DETAIL = {
    kind: 'detail',
    label: 'Surface detail',
    blurb: 'Rubble and footholds on the finished surface. Amplitude follows the FINAL slope, so treads '
        + 'stay smooth however steep the hillside under them is.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'frequency', label: 'Detail scale', min: 1, max: 20, step: 0.5, def: 10,
            desc: 'Surface-bump frequency. Higher = finer, more frequent rubble.' },
        { t: 'number', key: 'flat', label: 'Detail (flat)', min: 0, max: 2, step: 0.05, def: 0.05, unit: 'm',
            desc: 'Surface texture on flat ground. Higher = bumpier plains.' },
        { t: 'number', key: 'steep', label: 'Detail (steep)', min: 0, max: 10, step: 0.25, def: 5, unit: 'm',
            desc: 'Surface texture on steep slopes. Higher = more jagged mountains.' },
    ],
};
export const SEA = {
    kind: 'sea',
    label: 'Sea',
    blurb: 'Floods every column below the Ground level datum. The first water kind, and the shape '
        + 'rivers and cavern pools reuse when they return.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'material', key: 'material', label: 'Water', def: 'water',
            desc: 'The material every water body in the world is drawn with.' },
    ],
};
/**
 * THE SKIN, AND WHERE ITS BANDS SIT.
 *
 * All five of these were `mat('gravel' | 'sand' | 'rock' | 'snow' | 'moss2')` literals inside
 * `LandformField.surfaceMaterial` — five materials the world dialog had never been able to reach.
 * The two thresholds that decide which band a column lands in came with them: the snow line was on
 * `landform` and the rock slope was on `detail`, which is why the rule was hard to find at all.
 *
 * NOT THE SAME THING AS `strata`, and the two are easy to confuse because the default ground here
 * and the default top band there are both `moss2`. This is a few voxels of skin over the finished
 * landform surface; strata are the depth bands under EVERY column, flat worlds included.
 */
export const SURFACE = {
    kind: 'surface',
    label: 'Surface',
    blurb: 'What the finished landform is skinned with, and the elevation and slope the bands change at.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    params: [
        { t: 'material', key: 'ground', label: 'Ground', def: 'moss2',
            desc: 'Plains and hillsides — everything that is not beach, cliff, snow or seabed.' },
        { t: 'material', key: 'beach', label: 'Beach', def: 'sand',
            desc: 'The shelf between the waterline and Beach width.' },
        { t: 'material', key: 'seabed', label: 'Sea bed', def: 'gravel',
            desc: 'Everything below the waterline.' },
        { t: 'material', key: 'steep', label: 'Steep rock', def: 'rock',
            desc: 'Faces steeper than Rock slope. Overrides snow, so a cliff is bare at any height.' },
        { t: 'material', key: 'snow', label: 'Snow', def: 'snow',
            desc: 'Shallow ground above the snow line.' },
        { t: 'number', key: 'rockSlopeDeg', label: 'Rock slope', min: 20, max: 80, step: 5, def: 30, unit: 'deg',
            desc: 'Slope angle where ground turns to rock. Higher = less rock (only sheer cliffs).' },
        // Much above 0.6 the peaks stop REACHING it and snow disappears entirely — the continental
        // field is FBm, so the configured mountain height is a ceiling the terrain rarely touches.
        { t: 'number', key: 'snowLineFrac', label: 'Snow line', min: 0, max: 100, step: 5, def: 50, unit: '%',
            desc: 'Where snow starts, as a fraction of mountain height. Higher = less snow.' },
    ],
};
export const STRATA = {
    kind: 'strata',
    label: 'Ground layers',
    blurb: 'What the ground is made of, by depth below the surface — in every world, flat included. '
        + 'On a landform world the surface skin sits on top of these for the first few voxels.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    params: [
        { t: 'list', key: 'layers', label: 'Layers',
            item: [
                { t: 'material', key: 'material', label: 'Material', def: 'rock', desc: 'What this band is made of.' },
                { t: 'number', key: 'maxDepth', label: 'To depth', min: 0, max: 16, step: 0.25, def: 2, unit: 'm', desc: 'How far below the surface this band reaches.' },
            ],
            def: [
                { material: 'moss2', maxDepth: 0.5 },
                { material: 'rock', maxDepth: 2 },
            ],
            desc: 'Bands from the surface down. Anything deeper than the last band is the fallback below.' },
        { t: 'material', key: 'deep', label: 'Deep rock', def: 'rock2',
            desc: 'What everything below the last band is made of.' },
    ],
};
export const TERRAIN_KINDS = [
    WORLD, LANDFORM, TERRACE, DETAIL, SEA, SURFACE, STRATA,
];
//# sourceMappingURL=terrain.js.map